/**
 * Theme color utilities for organization customization.
 * Used to derive visual-separation tokens without altering
 * administrator-selected --theme-primary / --theme-primary-text.
 */

/** Typical content/card surface behind primary UI (Skote cards are white). */
export const APP_SURFACE_BACKGROUND = "#ffffff";

/** Page body background from theme ($body-bg). */
export const APP_BODY_BACKGROUND = "#f1f5f7";

/** UI non-text contrast below this needs extra separation (WCAG UI ~3:1; we use a lower gate for "visible edge"). */
export const PRIMARY_SURFACE_CONTRAST_THRESHOLD = 1.45;

const DEFAULT_PRIMARY = "#5664d2";

const parseHexChannel = (hex, start, length) => {
    const raw = hex.slice(start, start + length);
    const value = length === 1 ? `${raw}${raw}` : raw;
    return parseInt(value, 16);
};

/**
 * Parse a CSS color string into { r, g, b } (0–255).
 * Supports #rgb, #rrggbb, rgb(), rgba(). Returns null if unparseable.
 */
export const parseCssColor = (color) => {
    if (!color || typeof color !== "string") {
        return null;
    }

    const value = color.trim().toLowerCase();

    if (value.startsWith("#")) {
        const hex = value.slice(1);
        if (/^[0-9a-f]{3}$/i.test(hex)) {
            return {
                r: parseHexChannel(hex, 0, 1),
                g: parseHexChannel(hex, 1, 1),
                b: parseHexChannel(hex, 2, 1),
            };
        }
        if (/^[0-9a-f]{6}$/i.test(hex)) {
            return {
                r: parseHexChannel(hex, 0, 2),
                g: parseHexChannel(hex, 2, 2),
                b: parseHexChannel(hex, 4, 2),
            };
        }
        return null;
    }

    const rgbMatch = value.match(
        /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/
    );
    if (rgbMatch) {
        return {
            r: Math.min(255, Math.max(0, Number(rgbMatch[1]))),
            g: Math.min(255, Math.max(0, Number(rgbMatch[2]))),
            b: Math.min(255, Math.max(0, Number(rgbMatch[3]))),
        };
    }

    return null;
};

const srgbChannelToLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG 2.1 relative luminance for an { r, g, b } color. */
export const getRelativeLuminance = (rgb) => {
    if (!rgb) {
        return 0;
    }

    const r = srgbChannelToLinear(rgb.r);
    const g = srgbChannelToLinear(rgb.g);
    const b = srgbChannelToLinear(rgb.b);

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two CSS colors (or rgb objects). */
export const getContrastRatio = (colorA, colorB) => {
    const rgbA = typeof colorA === "string" ? parseCssColor(colorA) : colorA;
    const rgbB = typeof colorB === "string" ? parseCssColor(colorB) : colorB;

    if (!rgbA || !rgbB) {
        return 1;
    }

    const luminanceA = getRelativeLuminance(rgbA);
    const luminanceB = getRelativeLuminance(rgbB);
    const lighter = Math.max(luminanceA, luminanceB);
    const darker = Math.min(luminanceA, luminanceB);

    return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Whether primary needs extra separation against app surfaces.
 * Checks both white content surfaces and the page body background.
 */
export const primaryNeedsSurfaceSeparation = (
    primaryColor,
    {
        surfaces = [APP_SURFACE_BACKGROUND, APP_BODY_BACKGROUND],
        threshold = PRIMARY_SURFACE_CONTRAST_THRESHOLD,
    } = {}
) => {
    const primary = parseCssColor(primaryColor) || parseCssColor(DEFAULT_PRIMARY);

    return surfaces.some((surface) => {
        const ratio = getContrastRatio(primary, surface);
        return ratio < threshold;
    });
};

const rgbToHex = (rgb) => {
    const toHex = (channel) =>
        Math.round(Math.min(255, Math.max(0, channel)))
            .toString(16)
            .padStart(2, "0");

    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
};

/** Mix a color toward black by `amount` (0–1). Used to derive a border when needed. */
export const mixColorTowardBlack = (color, amount = 0.22) => {
    const rgb = parseCssColor(color) || parseCssColor(DEFAULT_PRIMARY);
    const mix = Math.min(1, Math.max(0, amount));

    return rgbToHex({
        r: rgb.r * (1 - mix),
        g: rgb.g * (1 - mix),
        b: rgb.b * (1 - mix),
    });
};

/** Bootstrap `shade-color($color, $weight)` — mix with black. Weight is 0–1. */
export const shadeColor = (color, weight = 0.15) =>
    mixColorTowardBlack(color, weight);

/** Bootstrap `tint-color($color, $weight)` — mix with white. Weight is 0–1. */
export const tintColor = (color, weight = 0.15) => {
    const rgb = parseCssColor(color) || parseCssColor(DEFAULT_PRIMARY);
    const mix = Math.min(1, Math.max(0, weight));

    return rgbToHex({
        r: rgb.r + (255 - rgb.r) * mix,
        g: rgb.g + (255 - rgb.g) * mix,
        b: rgb.b + (255 - rgb.b) * mix,
    });
};

/**
 * Bootstrap-like `color-contrast($background)` against white/black.
 * Uses this project's $min-contrast-ratio (1.7) as the preference gate.
 */
export const getColorContrast = (
    backgroundColor,
    {
        light = "#ffffff",
        dark = "#000000",
        minContrastRatio = 1.7,
    } = {}
) => {
    const lightRatio = getContrastRatio(backgroundColor, light);
    const darkRatio = getContrastRatio(backgroundColor, dark);

    if (lightRatio >= minContrastRatio || lightRatio >= darkRatio) {
        return light;
    }

    return dark;
};

const COLOR_CONTRAST_LIGHT = "#ffffff";

/**
 * Bootstrap / Reactstrap button hover+active amounts
 * (see $btn-hover-bg-*-amount / $btn-active-*-amount).
 */
export const BTN_HOVER_BG_SHADE_AMOUNT = 0.15;
export const BTN_HOVER_BG_TINT_AMOUNT = 0.15;
export const BTN_HOVER_BORDER_SHADE_AMOUNT = 0.2;
export const BTN_HOVER_BORDER_TINT_AMOUNT = 0.1;
export const BTN_ACTIVE_BG_SHADE_AMOUNT = 0.2;
export const BTN_ACTIVE_BG_TINT_AMOUNT = 0.2;
export const BTN_ACTIVE_BORDER_SHADE_AMOUNT = 0.25;
export const BTN_ACTIVE_BORDER_TINT_AMOUNT = 0.1;

/** Minimum contrast between base primary and hover bg so the state is visible. */
export const PRIMARY_HOVER_MIN_CONTRAST = 1.25;

/**
 * Stronger shade steps used when the Bootstrap 15% hover shift is still too subtle
 * (light / near-white primaries — same idea as Bootstrap's `.btn-light` override).
 */
export const BTN_HOVER_BG_SHADE_AMOUNT_VISIBLE = 0.22;
export const BTN_ACTIVE_BG_SHADE_AMOUNT_VISIBLE = 0.32;
export const BTN_HOVER_BORDER_SHADE_AMOUNT_VISIBLE = 0.28;
export const BTN_ACTIVE_BORDER_SHADE_AMOUNT_VISIBLE = 0.36;

/**
 * Reactstrap / Bootstrap `button-variant` hover & active colors for org Primary.
 *
 * Mirrors Bootstrap 5 / Reactstrap:
 * - shade when contrast text is light (dark primary) — default `button-variant`
 * - `.btn-light` special-case: shade light / low-contrast primaries (tint is invisible)
 * - `.btn-dark` special-case: tint near-black primaries (shade is invisible)
 * - bump shade/tint amounts when the default 15% hover shift is still too subtle
 *
 * Does not alter administrator-selected --theme-primary / --theme-primary-text.
 */
export const getPrimaryInteractionTokens = (primaryColor) => {
    const primary = primaryColor || DEFAULT_PRIMARY;
    const contrastFg = getColorContrast(primary);
    const isLightForeground =
        contrastFg.toLowerCase() === COLOR_CONTRAST_LIGHT;
    const luminance = getRelativeLuminance(
        parseCssColor(primary) || parseCssColor(DEFAULT_PRIMARY)
    );

    // Near-black primary → Bootstrap `.btn-dark`: tint (lighten) on hover
    if (luminance < 0.08) {
        const defaultHover = tintColor(primary, BTN_HOVER_BG_TINT_AMOUNT);
        const needsStronger =
            getContrastRatio(primary, defaultHover) < PRIMARY_HOVER_MIN_CONTRAST;
        const hoverAmt = needsStronger ? 0.28 : BTN_HOVER_BG_TINT_AMOUNT;
        const activeAmt = needsStronger ? 0.4 : BTN_ACTIVE_BG_TINT_AMOUNT;
        const hoverBorderAmt = needsStronger
            ? 0.22
            : BTN_HOVER_BORDER_TINT_AMOUNT;
        const activeBorderAmt = needsStronger
            ? 0.28
            : BTN_ACTIVE_BORDER_TINT_AMOUNT;
        const hoverBackground = tintColor(primary, hoverAmt);
        const activeBackground = tintColor(primary, activeAmt);

        return {
            useShade: false,
            hoverBackground,
            hoverBorder: tintColor(primary, hoverBorderAmt),
            hoverColor: getColorContrast(hoverBackground),
            activeBackground,
            activeBorder: tintColor(primary, activeBorderAmt),
            activeColor: getColorContrast(activeBackground),
        };
    }

    // Bootstrap `.btn-light` forces shade; also force shade for low-contrast primaries.
    const preferShade =
        !isLightForeground || primaryNeedsSurfaceSeparation(primary);

    if (!preferShade) {
        // Dark primary + light text → Bootstrap default: shade
        const hoverBackground = shadeColor(primary, BTN_HOVER_BG_SHADE_AMOUNT);
        const activeBackground = shadeColor(primary, BTN_ACTIVE_BG_SHADE_AMOUNT);
        const needsStronger =
            getContrastRatio(primary, hoverBackground) < PRIMARY_HOVER_MIN_CONTRAST;

        if (needsStronger) {
            const strongHover = shadeColor(
                primary,
                BTN_HOVER_BG_SHADE_AMOUNT_VISIBLE
            );
            const strongActive = shadeColor(
                primary,
                BTN_ACTIVE_BG_SHADE_AMOUNT_VISIBLE
            );

            return {
                useShade: true,
                hoverBackground: strongHover,
                hoverBorder: shadeColor(
                    primary,
                    BTN_HOVER_BORDER_SHADE_AMOUNT_VISIBLE
                ),
                hoverColor: getColorContrast(strongHover),
                activeBackground: strongActive,
                activeBorder: shadeColor(
                    primary,
                    BTN_ACTIVE_BORDER_SHADE_AMOUNT_VISIBLE
                ),
                activeColor: getColorContrast(strongActive),
            };
        }

        return {
            useShade: true,
            hoverBackground,
            hoverBorder: shadeColor(primary, BTN_HOVER_BORDER_SHADE_AMOUNT),
            hoverColor: getColorContrast(hoverBackground),
            activeBackground,
            activeBorder: shadeColor(primary, BTN_ACTIVE_BORDER_SHADE_AMOUNT),
            activeColor: getColorContrast(activeBackground),
        };
    }

    // Light primary + dark text → shade like `.btn-light` (not Bootstrap's default tint)
    const defaultHover = shadeColor(primary, BTN_HOVER_BG_SHADE_AMOUNT);
    const needsStronger =
        primaryNeedsSurfaceSeparation(primary) ||
        getContrastRatio(primary, defaultHover) < PRIMARY_HOVER_MIN_CONTRAST;

    const hoverBgAmount = needsStronger
        ? BTN_HOVER_BG_SHADE_AMOUNT_VISIBLE
        : BTN_HOVER_BG_SHADE_AMOUNT;
    const hoverBorderAmount = needsStronger
        ? BTN_HOVER_BORDER_SHADE_AMOUNT_VISIBLE
        : BTN_HOVER_BORDER_SHADE_AMOUNT;
    const activeBgAmount = needsStronger
        ? BTN_ACTIVE_BG_SHADE_AMOUNT_VISIBLE
        : BTN_ACTIVE_BG_SHADE_AMOUNT;
    const activeBorderAmount = needsStronger
        ? BTN_ACTIVE_BORDER_SHADE_AMOUNT_VISIBLE
        : BTN_ACTIVE_BORDER_SHADE_AMOUNT;

    const hoverBackground = shadeColor(primary, hoverBgAmount);
    const activeBackground = shadeColor(primary, activeBgAmount);

    return {
        useShade: true,
        hoverBackground,
        hoverBorder: shadeColor(primary, hoverBorderAmount),
        hoverColor: getColorContrast(hoverBackground),
        activeBackground,
        activeBorder: shadeColor(primary, activeBorderAmount),
        activeColor: getColorContrast(activeBackground),
    };
};

/**
 * Apply Bootstrap/Reactstrap-aligned hover & active CSS variables on :root.
 * Leaves --theme-primary / --theme-primary-text / --color-* untouched.
 */
export const applyPrimaryInteractionVars = (
    primaryColor,
    root = typeof document !== "undefined" ? document.documentElement : null
) => {
    const tokens = getPrimaryInteractionTokens(primaryColor);

    if (!root) {
        return tokens;
    }

    root.style.setProperty("--theme-primary-hover", tokens.hoverBackground);
    root.style.setProperty(
        "--theme-primary-hover-border",
        tokens.hoverBorder
    );
    root.style.setProperty("--theme-primary-text-hover", tokens.hoverColor);
    root.style.setProperty("--theme-primary-active", tokens.activeBackground);
    root.style.setProperty(
        "--theme-primary-active-border",
        tokens.activeBorder
    );
    root.style.setProperty("--theme-primary-text-active", tokens.activeColor);

    return tokens;
};

/**
 * Derived theme tokens for primary/surface separation.
 * Does not alter the selected primary or primary-text colors.
 *
 * - borderColor: subtle edge on Primary-filled surfaces
 * - outlineColor: stronger foreground for outline buttons (transparent BG)
 * Both reuse the same contrast gate; only the mix amount differs.
 */
export const getPrimarySurfaceSeparationTokens = (primaryColor) => {
    const primary = primaryColor || DEFAULT_PRIMARY;
    const needsSeparation = primaryNeedsSurfaceSeparation(primary);

    if (!needsSeparation) {
        return {
            needsSeparation: false,
            borderColor: primary,
            outlineColor: primary,
            surfaceShadow: "none",
        };
    }

    return {
        needsSeparation: true,
        borderColor: mixColorTowardBlack(primary, 0.22),
        // Stronger mix so outline border/text stay readable on light pages
        outlineColor: mixColorTowardBlack(primary, 0.5),
        surfaceShadow: "0 1px 2px color-mix(in srgb, #000 10%, transparent)",
    };
};

/**
 * Apply derived primary separation CSS variables on :root.
 * Leaves --theme-primary / --theme-primary-text / --color-* untouched.
 */
export const applyPrimarySurfaceSeparationVars = (
    primaryColor,
    root = typeof document !== "undefined" ? document.documentElement : null
) => {
    if (!root) {
        return getPrimarySurfaceSeparationTokens(primaryColor);
    }

    const tokens = getPrimarySurfaceSeparationTokens(primaryColor);

    root.style.setProperty("--theme-primary-border", tokens.borderColor);
    root.style.setProperty("--theme-primary-outline", tokens.outlineColor);
    root.style.setProperty("--theme-primary-surface-shadow", tokens.surfaceShadow);
    root.setAttribute(
        "data-theme-primary-contrast",
        tokens.needsSeparation ? "low" : "ok"
    );

    return tokens;
};

/**
 * Convert a CSS color to a Bootstrap RGB channel list: "r, g, b".
 * Used for --bs-primary-rgb so .bg-primary / .text-primary stay theme-aware.
 */
export const cssColorToRgbChannels = (color) => {
    const rgb = parseCssColor(color) || parseCssColor(DEFAULT_PRIMARY);
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
};

/**
 * Sync Bootstrap Primary CSS variables with the org Primary Color.
 * Enables .bg-primary / .text-primary (and other --bs-primary-rgb consumers)
 * without changing semantic success/warning/danger/info tokens.
 * Does not alter --theme-primary / --theme-primary-text.
 */
export const applyBootstrapPrimaryUtilityVars = (
    primaryColor,
    root = typeof document !== "undefined" ? document.documentElement : null
) => {
    const primary = primaryColor || DEFAULT_PRIMARY;
    const channels = cssColorToRgbChannels(primary);

    if (!root) {
        return { primary, channels };
    }

    root.style.setProperty("--bs-primary", primary);
    root.style.setProperty("--bs-primary-rgb", channels);

    return { primary, channels };
};
